import { Builder } from '@builder.io/react';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedCurrency } from '../../../../../store/user';
import { selectProducts } from '../../../../../store/products';
import useHandleAddProduct from '../../../../../utils/handleAddProduct';
import useCart from '../../../../../hooks/cart/useCart';
import ReactSelect from 'react-select';
import SelectCurrency from '../../../../general/selectCurrency';
import ShowCurrencies from '../../../../../utils/showCurrencies';
import { openSheet } from '../../../../../store/sheetToggler';
import ConvertUSD from '../../../../../utils/convertUSD';

const styles = {
    control: (base) => ({
        ...base,
        height: '48px',
        border: 'none',
        boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.10)'
    }),
    menuList: (base) => ({
        ...base,
        '::-webkit-scrollbar': {
            width: '6px',
            height: '0px',
        },
        '::-webkit-scrollbar-track': {
            background: '#f1f1f1',
        },
        '::-webkit-scrollbar-thumb': {
            background: '#888',
        },
        '::-webkit-scrollbar-thumb:hover': {
            background: '#555',
        },
    }),
    shadow: {
        boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.10)'
    }
};

const SadaqahJariaWidget = ({ defaultProduct, dropDownProducts }) => {
    const selectedCurrencyStore = useSelector(selectSelectedCurrency);
    const products = useSelector((state) => selectProducts(state));
    const [productAmount, setProductAmount] = useState('');
    const [isLoaded, setIsLoaded] = useState(false);
    const [currentCurrency, setCurrentCurrency] = useState();
    const [productsList, setProductsList] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(defaultProduct);
    const [currentProduct, setCurrentProduct] = useState(defaultProduct);
    const [addProduct] = useHandleAddProduct();
    const { handleAddToGiving: handleAddToGivingCart } = useCart();
    const dispatch = useDispatch();

    // Recurring modes
    const suggestedRecurringModes = [
        { value: 'one-off', label: 'Single' },
        { value: 'week', label: 'Weekly' },
        { value: 'month', label: 'Monthly' }
    ];
    const [selectedRecurring, setSelectedRecurring] = useState(suggestedRecurringModes[0]);

    // Suggested prices (fixed as in SadaqahWidget)
    const [selectedPrice, setSelectedPrice] = useState({
        value: 50,
        index: 1
    });
    // Dynamic suggested prices based on currency
    const getSuggestedPrices = (currency) => [
        { value: handleConvert(currency, 50), label: ShowCurrencies(currency, handleConvert(currency, 50), false) },
        { value: handleConvert(currency, 100), label: ShowCurrencies(currency, handleConvert(currency, 100), false) },
        { value: handleConvert(currency, 150), label: ShowCurrencies(currency, handleConvert(currency, 150), false) },
    ];
    const handleConvert = (currency, value) => {
        return Math.round(ConvertUSD(currency, value, false));
    };
    const [suggestedPrices, setSuggestedPrices] = useState(getSuggestedPrices(currentCurrency));

    // Parse dropDownProducts (comma separated creator codes)
    const dropDownList = useMemo(() => {
        return Array.isArray(dropDownProducts)
            ? dropDownProducts
            : typeof dropDownProducts === 'string'
                ? dropDownProducts.split(/\s*,\s*/).filter(Boolean)
                : [];
    }, [dropDownProducts]);

    // Get all products for dropdown
    useEffect(() => {
        if (products && products.length) {
            setIsLoaded(true);
            let tempMakeDonationProducts = [];
            let tempProductsList = [];
            let tempDefaultProduct;
            products.forEach(item => {
                item.sub_categories.forEach(inner_item => {
                    inner_item.products.forEach(inner_item_product => {
                        if (dropDownList.includes(inner_item_product.creator)) {
                            tempMakeDonationProducts.push(inner_item_product);
                            tempProductsList.push({
                                value: inner_item_product.id,
                                label: inner_item_product.name,
                                creator: inner_item_product.creator
                            });
                        }
                        if (inner_item_product.creator === defaultProduct.creator) {
                            tempDefaultProduct = inner_item_product;
                        }
                    });
                });
            });
            // Set default product at the top
            if (tempDefaultProduct) {
                tempProductsList = tempProductsList.filter(p => p.creator !== tempDefaultProduct.creator);
                tempProductsList.unshift({
                    value: tempDefaultProduct.id,
                    label: tempDefaultProduct.name,
                    creator: tempDefaultProduct.creator
                });
                setSelectedProduct(tempDefaultProduct);
                setCurrentProduct({
                    value: tempDefaultProduct.id,
                    label: tempDefaultProduct.name,
                    creator: tempDefaultProduct.creator
                });
            }
            setProductsList(tempProductsList);
            setAllProducts(tempMakeDonationProducts);
        }
    }, [products, dropDownList, defaultProduct]);

    useEffect(() => {
        if (selectedCurrencyStore) {
            setCurrentCurrency(selectedCurrencyStore);
        } else {
            setCurrentCurrency('AUD');
        }
    }, [selectedCurrencyStore]);

    // Update suggested prices and selectedPrice when currency changes
    useEffect(() => {
        setSuggestedPrices(getSuggestedPrices(currentCurrency));
        // Reset selectedPrice to first price when currency changes
        setSelectedPrice({ value: handleConvert(currentCurrency, 50), index: 1 });
    }, [currentCurrency]);

    // Handlers
    const handleCurrency = (currency) => {
        setCurrentCurrency(currency.value);
    };
    const handleSelectedProduct = (product) => {
        allProducts.forEach(item => {
            if (item.id === product.value) setSelectedProduct(item);
        });
        setCurrentProduct(product);
    };
    const handleSelectedItem = (currency, value, index) => {
        setSelectedPrice({ value, index });
    };
    const handleAddNewProduct = () => {
        let tempProduct = selectedProduct;
        let customAmount = Number(productAmount) ? Number(productAmount) : Number(selectedPrice.value) ? Number(selectedPrice.value) : 50;
        tempProduct = Object.assign({}, tempProduct, {
            quantity: customAmount,
            recurring: selectedRecurring.value,
        });
        handleAddToGivingCart(tempProduct, 'custom');
        dispatch(openSheet());
    };

    return (
        <div className={`w-full rounded-lg bg-slate-200- py-2 md:py-4 flex flex-wrap items-center`}>
            <div className="basis-full grid grid-cols-12 gap-2">
                <div className="col-span-12 min-[500px]:col-span-6 lg:col-span-4 lg:mb-2 flex items-center justify-center" style={styles.shadow}>
                    <ReactSelect
                        options={productsList}
                        className='max-h-[48px] w-full'
                        value={currentProduct}
                        isSearchable={false}
                        styles={styles}
                        placeholder='Select Product'
                        onChange={handleSelectedProduct}
                    />
                </div>
                <div className="col-span-12 min-[500px]:col-span-3 lg:col-span-4 lg:mb-2 flex items-center justify-center" style={styles.shadow}>
                    <ReactSelect
                        options={suggestedRecurringModes}
                        className='[&>*]:!cursor-pointer w-full'
                        value={selectedRecurring}
                        isSearchable={false}
                        styles={styles}
                        onChange={setSelectedRecurring}
                    />
                </div>
                <div className="col-span-12 min-[500px]:col-span-3 lg:col-span-4 lg:mb-2 flex items-center justify-center" style={styles.shadow}>
                    <SelectCurrency onChangeCurrency={handleCurrency} classNamePrefix="orphan_select" />
                </div>
                <div className="col-span-12 min-[500px]:col-span-12 lg:col-span-4 md:grid grid-cols-12 gap-2 mb-2 lg:mb-8">
                    {suggestedPrices.map((item, index) => (
                        <div key={`prices${index}`} className="col-span-12 sm:col-span-4 xl:col-span-4 mb-1 md:mb-0">
                            <div
                                className={`h-12 text-lg md:text-xl transition-all duration-200 cursor-pointer flex justify-center items-center px-2 md:px-4 py-2 md:py-4 min-w-[80px] border border-[#F60362] ${selectedPrice && selectedPrice.value === item.value ? 'bg-[#F60362] text-white' : 'bg-white text-[#F60362] hover:bg-[#F60362] hover:text-white'}`}
                                onClick={() => {
                                    setSelectedPrice({ value: item.value, index: index + 1 });
                                    setProductAmount('');
                                }}
                            >
                                {ShowCurrencies(currentCurrency, item.value, true)}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="col-span-12 min-[400px]:col-span-6 lg:col-span-4 flex justify-center items-center bg-white rounded-md h-12 px-2 mb-2 min-[400px]:mb-0" style={styles.shadow}>
                    <span className="text-sm border-r pr-2 text-[#78716C]">{currentCurrency}</span>
                    <input
                        value={productAmount}
                        className="w-full p-2 rounded-lg text-base focus:outline-none focus:ring-1 focus:ring-transparent focus:border-transparent"
                        type="number"
                        onChange={e => {
                            setProductAmount(e.target.value);
                            setSelectedPrice(null);
                        }}
                        placeholder="Enter amount"
                    />
                </div>
                <div className="col-span-12 lg:col-span-4 flex justify-center items-center-">
                    <button
                        className={`text-white w-full text-sm md:text-lg transition-all duration-200 flex items-center justify-center h-12 px-3 rounded-md ${isLoaded ? 'bg-[#f60362]' : 'bg-stone-300 cursor-not-allowed'}`}
                        onClick={handleAddNewProduct}
                        disabled={!isLoaded}
                    >Quick Donate</button>
                </div>
            </div>
        </div>
    );
};

Builder.registerComponent(SadaqahJariaWidget, {
    name: 'SadaqahJariaWidget',
    inputs: [
        {
            name: 'defaultProduct',
            type: 'object',
            defaultValue: {
                creator: '',
                label: '',
                value: null,
            },
            friendlyName: 'Default Product Info',
            subFields: [
                {
                    name: 'creator',
                    type: 'string',
                    helperText: 'Enter product creator ID',
                    required: true,
                    hideFromUI: false,
                },
                {
                    name: 'label',
                    type: 'string',
                    helperText: 'Display name for the product',
                    required: true,
                    hideFromUI: false,
                    friendlyName: 'Product Label',
                },
                {
                    name: 'value',
                    type: 'number',
                    helperText: 'Product reference value (usually product ID)',
                    required: true,
                    hideFromUI: false,
                    friendlyName: 'Product Value ID',
                },
            ],
        },
        {
            name: 'dropDownProducts',
            type: 'string',
            required: true,
            helperText: 'Enter product creator codes (comma separated)',
            friendlyName: 'Products Dropdown',
        },
    ],
});

export default SadaqahJariaWidget;
